<script setup lang="ts">
import { computed, inject, reactive, ref, type Ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { inventoryApi, type InventoryItem, type InventoryItemForm } from '@/api/inventory';
import type { SerializedUser } from '@/api/users';
import { apiErrorText } from '@/utils/api-errors';
import { UNITS_OF_MEASURE } from '@/utils/inventory';
import PriceInput from '@/components/price-input.vue';

const props = defineProps<{ item?: InventoryItem | null }>();
const emit = defineEmits<{ close: []; saved: [item: InventoryItem] }>();

const queryClient = useQueryClient();
const isEditing = computed(() => !!props.item);
// Name, unit and tracking of a medication come from the medication catalog.
const isMedication = computed(() => !!props.item?.clinicalDrugId);
const currentUser = inject<Ref<SerializedUser>>('currentUser');
const nameHint = computed(() => {
  if (isMedication.value) return 'Viene del catálogo de medicamentos y no se edita.';
  if (currentUser?.value?.companyMedicationsEnabled) {
    return 'Nombre genérico, sin marca. Ej.: Guante nitrilo M. Los medicamentos se agregan con «Agregar medicamento».';
  }

  return 'Nombre genérico, sin marca. Ej.: Paracetamol 500 mg, Guante nitrilo M';
});

const form = reactive<InventoryItemForm>({
  name: props.item?.name ?? '',
  inventoryCategoryId: props.item?.inventoryCategoryId,
  tracking: props.item?.tracking ?? 'quantity',
  unitOfMeasure: props.item?.unitOfMeasure ?? 'unit',
  defaultUnitCostCents: props.item?.defaultUnitCostCents ?? null,
  code: props.item?.code ?? '',
  description: props.item?.description ?? '',
});
const error = ref('');

const { data: categories } = useQuery({
  queryKey: ['inventoryCategories', 'active'],
  queryFn: () => inventoryApi.categories(),
  initialData: [],
});

const trackings = [
  { value: 'quantity', title: 'Por cantidad (insumos, medicamentos)' },
  { value: 'serialized', title: 'Por unidad, con identificador (camas, sillas, equipos)' },
];

const save = useMutation({
  mutationFn: () => inventoryApi.saveItem({ ...form, id: props.item?.id }),
  onSuccess: (item) => {
    queryClient.invalidateQueries({ queryKey: ['inventoryItems'] });
    emit('saved', item);
    emit('close');
  },
  onError: (failure) => {
    error.value = apiErrorText(failure, 'No se pudo guardar el artículo.');
  },
});

const valid = computed(() => form.name.trim().length > 0 && !!form.inventoryCategoryId);
</script>
<template>
  <v-card :title="isEditing ? 'Editar artículo' : 'Nuevo artículo'">
    <v-card-text class="pt-5">
      <div class="text-subtitle-2 font-weight-bold mb-3">
        Artículo
      </div>
      <v-text-field
        v-model="form.name"
        label="Nombre"
        :hint="nameHint"
        persistent-hint
        :disabled="isMedication"
        variant="outlined"
        density="compact"
        maxlength="160"
        class="mb-3"
      />
      <v-row dense>
        <v-col
          cols="12"
          sm="6"
        >
          <v-select
            v-model="form.inventoryCategoryId"
            :items="categories"
            item-title="name"
            item-value="id"
            label="Categoría"
            variant="outlined"
            density="compact"
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-text-field
            v-model="form.code"
            label="Código interno (opcional)"
            variant="outlined"
            density="compact"
          />
        </v-col>
      </v-row>

      <v-divider class="my-4" />
      <div class="text-subtitle-2 font-weight-bold mb-3">
        Control y consumo
      </div>
      <v-row dense>
        <v-col
          cols="12"
          sm="6"
        >
          <v-select
            v-model="form.tracking"
            :items="trackings"
            label="Control"
            variant="outlined"
            density="compact"
            :disabled="isMedication"
            hint="No se puede cambiar una vez que el artículo tiene movimientos"
            persistent-hint
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-select
            v-model="form.unitOfMeasure"
            :items="UNITS_OF_MEASURE"
            label="Unidad de consumo"
            variant="outlined"
            density="compact"
            :disabled="isMedication"
            hint="Lo que realmente se usa: una caja de 100 guantes son 100 unidades"
            persistent-hint
          />
        </v-col>
      </v-row>
      <price-input
        :model-value="form.defaultUnitCostCents ?? 0"
        label="Costo unitario sugerido (opcional)"
        density="compact"
        class="mt-3"
        @update:model-value="form.defaultUnitCostCents = $event || null"
      />

      <v-divider class="my-4" />
      <v-textarea
        v-model="form.description"
        label="Descripción (opcional)"
        variant="outlined"
        density="compact"
        rows="2"
        auto-grow
      />
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        class="mt-3"
        :text="error"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="save.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :disabled="!valid"
        :loading="save.isPending.value"
        @click="save.mutate()"
      >
        {{ isEditing ? 'Guardar' : 'Crear' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
